import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InstallPWA } from "@/components/InstallPWA";
import {
  Building2, BarChart3, MessageCircle, Target, TrendingUp,
  Landmark, Hammer, Calculator, Users, Bot, Lock,
  ExternalLink, BookOpen, UserCircle, LogOut, ChevronRight,
  Sparkles, ArrowRight, GraduationCap, History, Star, Bell, Settings, Moon, Sun,
  MapPin, Scale, FileText, Sailboat, Anchor, Compass, Globe, Search, Brain, Zap
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENT_AVATARS = {
  routeur: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/8a32cad401844db2c660f0152ac9e85b5847bbde3ca554477ea01904f8465f9b.png",
  analyse: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/19a86bf97520c7466c87f96c95a7fb5682566a3bb16f679be5d814a1aaebd419.png",
  ia: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/5c3ff245316d2ec62c555a457b600242ef0de940248d2c53a85d86424d732f1c.png",
  strategie: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/a847752862b35df5e195ab26f0a225f02d9e2ee6159f7a7a1e53694a20a7cdab.png",
  nego: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4889000b20dcadf5cae0644c4005446f0f2df56d3e42f67d7c61bd19baef64d8.png",
  banque: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4348a57bda3ad71e0c15bfa2f66c4feb4891a609193bab30cbe74908226f41a9.png",
  travaux: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/27f670b872ef630033ea86eddb35f1a5cbbfba487ba70b64919bec800d6785d1.png",
  fiscalite: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/79686ac1613549b6d0093e4da9dec98ef8ee543744a38527a4d24646f507da62.png",
  locataire: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/cd83e63bc00b54b2729dfa7347ffac350c80bf94cfe84c6c2f1955c58acd9faa.png",
  marche: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/4eeb12fb01e2fa4178fa72384eafe29396c02d2fe9477f7d1174207db4a453ba.png",
  juridique: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/b9295ba9fe52e4da445b0eb8e245b1ee8f3c5ca69956a48976b72ab78714f30a.png",
  notaire: "https://static.prod-images.emergentagent.com/jobs/0c949b6c-2246-4cdd-8394-1b138055d0de/images/0e489a0250e58a57ee0224da1c1e4af8d80ee0b8af54036914406cf159d489f6.png",
};

const ACTIVE_AGENTS = [
  {
    key: "routeur",
    name: "ODYS'GUIDE",
    desc: "Ton guide personnel. Je t'oriente et je cree ton plan d'action.",
    icon: Target,
    color: "#16233B",
    bgColor: "bg-[#16233B]/5",
    avatar: AGENT_AVATARS.routeur,
  },
  {
    key: "analyse",
    name: "ODYS'ANALYSE",
    desc: "Analyse la rentabilite de ton bien immobilier.",
    icon: BarChart3,
    color: "#FF8C42",
    bgColor: "bg-[#FF8C42]/5",
    avatar: AGENT_AVATARS.analyse,
  },
  {
    key: "ia",
    name: "ODYS'ASSISTANT",
    desc: "Ton assistant 24/7 pour toutes tes questions immo.",
    icon: MessageCircle,
    color: "#10B981",
    bgColor: "bg-emerald-50",
    avatar: AGENT_AVATARS.ia,
  },
  {
    key: "strategie",
    name: "ODYS'STRATEGIE",
    desc: "Definis ta strategie d'investissement personnalisee.",
    icon: TrendingUp,
    color: "#6366F1",
    bgColor: "bg-indigo-50",
    avatar: AGENT_AVATARS.strategie,
  },
  {
    key: "nego",
    name: "ODYS'NEGO",
    desc: "Prepare ta negociation immobiliere.",
    icon: Sparkles,
    color: "#EC4899",
    bgColor: "bg-pink-50",
    avatar: AGENT_AVATARS.nego,
  },
  {
    key: "banque",
    name: "ODYS'BANQUE",
    desc: "Credit, courtier et dossier bancaire.",
    icon: Landmark,
    color: "#0EA5E9",
    bgColor: "bg-sky-50",
    avatar: AGENT_AVATARS.banque,
  },
  {
    key: "travaux",
    name: "ODYS'TRAVAUX",
    desc: "Estime le budget travaux de ton bien.",
    icon: Hammer,
    color: "#D97706",
    bgColor: "bg-amber-50",
    avatar: AGENT_AVATARS.travaux,
  },
  {
    key: "fiscalite",
    name: "ODYS'FISCALITE",
    desc: "Optimise ton regime fiscal immobilier.",
    icon: Calculator,
    color: "#7C3AED",
    bgColor: "bg-violet-50",
    avatar: AGENT_AVATARS.fiscalite,
  },
  {
    key: "locataire",
    name: "ODYS'LOCATAIRE",
    desc: "Selection locataire et gestion locative.",
    icon: Users,
    color: "#059669",
    bgColor: "bg-emerald-50",
    avatar: AGENT_AVATARS.locataire,
  },
  {
    key: "marche",
    name: "ODYS'MARCHE",
    desc: "Analyse de marche et comparaison de villes.",
    icon: MapPin,
    color: "#14B8A6",
    bgColor: "bg-teal-50",
    avatar: AGENT_AVATARS.marche,
  },
  {
    key: "juridique",
    name: "ODYS'JURIDIQUE",
    desc: "Contrats, baux, litiges et questions juridiques.",
    icon: Scale,
    color: "#8B5CF6",
    bgColor: "bg-purple-50",
    avatar: AGENT_AVATARS.juridique,
  },
  {
    key: "notaire",
    name: "ODYS'NOTAIRE",
    desc: "Demarches notariales, signature et frais.",
    icon: FileText,
    color: "#374151",
    bgColor: "bg-gray-50",
    avatar: AGENT_AVATARS.notaire,
  },
];

const LOCKED_AGENTS = [];

const RESOURCES = [
  { label: "Communaute Odysinvest", url: "https://communaute-odys.odysinvest.com", icon: Users },
  { label: "OdysBoard (gestion)", url: "https://odys-board.odysinvest.com/login-styled.html", icon: BarChart3 },
];

export default function DashboardPage() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifyDialog, setNotifyDialog] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState("");

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await api().get("/dashboard");
      setDashboard(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement du dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleNotify = async () => {
    if (!notifyEmail) return;
    try {
      await axios.post(`${API}/notify`, { email: notifyEmail, agent_type: notifyDialog });
      toast.success("Tu seras notifie du lancement !");
      setNotifyDialog(null);
      setNotifyEmail("");
    } catch {
      toast.error("Erreur, reessaie plus tard");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-[#16233B]/20 border-t-[#16233B] rounded-full animate-spin" />
      </div>
    );
  }

  const d = dashboard || {};
  const programLabel = d.program === "admin" ? "Admin" : d.program === "major" ? "Major" : d.program === "odyss_mentoring" ? "Odyss'Mentoring" : "Odyss'Start";
  const isAdmin = d.program === "admin" || d.role === "admin";
  const completedCount = d.completed_modules?.length || 0;

  return (
    <div className="min-h-screen bg-[#F5F5F5] dark:bg-background" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white dark:bg-card border-b border-slate-200 dark:border-border sticky top-0 z-30 safe-top">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3" data-onboarding="welcome">
            <div className="w-8 h-8 rounded-lg bg-[#16233B] dark:bg-primary flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-lg font-bold text-[#16233B] dark:text-foreground font-heading hidden sm:block">
              Odys'<span className="text-[#F5B731]">IA</span>
            </span>
          </div>
          <div className="flex items-center gap-1 md:gap-2" data-onboarding="profile">
            {user?.role === "admin" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin")}
                data-testid="admin-link-btn"
                className="text-[#16233B] dark:text-foreground border-[#16233B]/20 dark:border-border hover:bg-[#16233B]/5 dark:hover:bg-accent/10 text-xs md:text-sm"
              >
                Admin
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/favorites")}
              className="text-[#6B7280] hover:text-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-500/10 touch-target hidden sm:flex"
            >
              <Star className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/conversations")}
              className="text-[#6B7280] hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 touch-target hidden sm:flex"
            >
              <History className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/notifications")}
              className="text-[#6B7280] hover:text-primary hover:bg-primary/5 dark:hover:bg-primary/10 touch-target"
            >
              <Bell className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/profile")}
              className="text-[#6B7280] hover:text-primary hover:bg-primary/5 dark:hover:bg-primary/10 touch-target"
            >
              <Settings className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
              className="text-[#6B7280] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 touch-target"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8 space-y-6 md:space-y-8 safe-bottom">
        {/* Welcome + Progress */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-slate-200 dark:border-border shadow-sm rounded-xl">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="space-y-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading">
                    Salut {d.student_name?.split(" ")[0] || "Major"} !
                  </h1>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={`${isAdmin ? "bg-[#FF8C42] text-white" : "bg-[#16233B] text-white"} border-0 rounded-full text-xs px-3`}>
                      {programLabel}
                    </Badge>
                    {d.enrollment_date && (
                      <span className="text-sm text-muted-foreground">
                        Inscrit depuis le {new Date(d.enrollment_date).toLocaleDateString("fr-FR")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress and Stats removed for students per user request */}

              {/* Stats row - only for students - simplified */}
              {!isAdmin && (
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#16233B] dark:text-[#FF8C42]">{d.analyzed_properties || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Biens analyses</p>
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#16233B] dark:text-[#FF8C42]">{d.total_conversations || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Conversations</p>
                </div>
              </div>
              )}

              {/* Admin quick stats */}
              {isAdmin && (
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#16233B] dark:text-[#FF8C42]">{d.total_students || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Total eleves</p>
                </div>
                <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#16233B] dark:text-[#FF8C42]">{d.total_conversations || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">Tes conversations test</p>
                </div>
              </div>
              )}
            </CardContent>
          </Card>

          {/* Next Action */}
          <Card className="border-[#FF8C42]/30 dark:border-[#FF8C42]/20 shadow-sm rounded-xl bg-gradient-to-br from-[#FF8C42]/5 to-transparent dark:from-[#FF8C42]/10">
            <CardContent className="p-6 flex flex-col justify-between h-full">
              <div>
                <div className="w-10 h-10 rounded-lg bg-[#FF8C42]/10 flex items-center justify-center mb-4">
                  <Sparkles className="w-5 h-5 text-[#FF8C42]" strokeWidth={1.5} />
                </div>
                <h3 className="font-bold text-foreground font-heading text-lg">Prochaine action</h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed">
                  {d.next_action || "Continue ton parcours !"}
                </p>
              </div>
              <Button
                onClick={() => navigate(isAdmin ? "/agent/analyse" : "/agent/analyse")}
                data-testid="next-action-btn"
                className="mt-6 w-full bg-[#FF8C42] hover:bg-[#e07a35] text-white font-medium rounded-lg active:scale-[0.98] transition-transform"
              >
                {isAdmin ? "Tester un agent" : "Analyser un bien"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </section>

        {/* Quick Links - Mobile friendly */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-slate-200 dark:border-border"
            onClick={() => navigate("/conversations")}
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center mb-2">
                <History className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-sm font-medium">Historique</span>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-slate-200 dark:border-border"
            onClick={() => navigate("/favorites")}
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-500/20 flex items-center justify-center mb-2">
                <Star className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <span className="text-sm font-medium">Favoris</span>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-slate-200 dark:border-border"
            onClick={() => navigate("/notifications")}
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center mb-2">
                <Bell className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="text-sm font-medium">Notifications</span>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow border-slate-200 dark:border-border"
            onClick={() => navigate("/profile")}
          >
            <CardContent className="p-4 flex flex-col items-center text-center">
              <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-500/20 flex items-center justify-center mb-2">
                <Settings className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              </div>
              <span className="text-sm font-medium">Parametres</span>
            </CardContent>
          </Card>
        </section>

        {/* BANNIERE — INTELLIGENCE ADAPTATIVE (informative, aucun lancement manuel) */}
          <div
            data-testid="adaptive-intelligence-banner"
            className="relative overflow-hidden rounded-xl mb-6 md:mb-8"
            style={{
              background: "linear-gradient(135deg, #16233B 0%, #1d2e4d 55%, #16233B 100%)",
              border: "1px solid #B5894A",
              boxShadow: "0 18px 50px -28px rgba(22, 35, 59, 0.55)",
            }}
          >
            <span
              aria-hidden="true"
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{ background: "#B5894A" }}
            />
            <span
              aria-hidden="true"
              className="absolute -right-16 -top-16 w-56 h-56 rounded-full opacity-20"
              style={{ background: "radial-gradient(circle, #B5894A 0%, transparent 70%)" }}
            />
            <div className="relative p-5 md:p-7">
              <div className="flex flex-col md:flex-row md:items-start gap-5 md:gap-6">
                <div
                  className="w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: "linear-gradient(145deg, #B5894A 0%, #8C6635 100%)",
                    border: "1px solid rgba(255,255,255,0.12)",
                  }}
                >
                  <Sailboat className="w-7 h-7 md:w-8 md:h-8 text-white" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[0.62rem] md:text-[0.66rem] font-semibold uppercase mb-1"
                    style={{ color: "#E9DCC4", letterSpacing: "0.28em" }}
                  >
                    Intelligence d'equipage
                  </div>
                  <h3
                    className="text-xl md:text-2xl font-bold leading-tight"
                    style={{ fontFamily: "var(--serif, 'Fraunces')", color: "#FAF7F1" }}
                  >
                    Tu poses la question. Eux decident du niveau.
                  </h3>
                  <p
                    className="text-sm md:text-[0.95rem] mt-2 leading-relaxed"
                    style={{ color: "#C8CFDC", maxWidth: "70ch" }}
                  >
                    Une question de methode ? Tu as ta reponse dans la seconde. Un chiffre de
                    marche, un taux, une regle qui a bouge ? Ton expert va verifier les donnees
                    du jour et te cite ses sources. Une vraie decision d'investissement ? Il
                    convoque de lui-meme ses confreres : plusieurs specialistes se prononcent,
                    ODYS'GUIDE tranche, et tu repars avec une position claire.
                    Rien a activer, rien a choisir : ils jugent ce que ta question merite.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {[
                      { icon: Zap, label: "Reponse directe" },
                      { icon: Compass, label: "Analyse verifiee et sourcee" },
                      { icon: Users, label: "Conseil collegial et arbitrage" },
                    ].map(({ icon: PillIcon, label }) => (
                      <span
                        key={label}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.7rem] font-semibold"
                        style={{
                          background: "rgba(181, 137, 74, 0.14)",
                          border: "1px solid rgba(181, 137, 74, 0.45)",
                          color: "#E9DCC4",
                        }}
                      >
                        <PillIcon className="w-3 h-3" />
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Active Agents */}
        <section data-onboarding="agents">
          <h2 className="text-lg md:text-xl font-bold text-[#1F2937] dark:text-foreground font-heading mb-4">Tes experts IA</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {ACTIVE_AGENTS.map((agent, idx) => {
              const Icon = agent.icon;
              const isGuide = agent.key === "routeur";
              return (
                <Card
                  key={agent.key}
                  className={`border-slate-200 dark:border-border shadow-sm rounded-xl cursor-pointer hover:shadow-lg hover:border-[#16233B]/30 dark:hover:border-primary/30 group overflow-hidden transition-all duration-300 ${isGuide ? "ring-2 ring-[#FF8C42]/50" : ""}`}
                  onClick={() => navigate(`/agent/${agent.key}`)}
                  data-testid={`agent-card-${agent.key}`}
                  data-onboarding={isGuide ? "guide" : undefined}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="relative shrink-0">
                        {agent.avatar ? (
                          <div className="relative avatar-entrance" style={{ animationDelay: `${idx * 0.15}s` }}>
                            <img
                              src={agent.avatar}
                              alt={agent.name}
                              className="w-14 h-14 rounded-xl object-cover ring-2 ring-offset-2 transition-all duration-300 group-hover:scale-110 group-hover:ring-4 avatar-float"
                              style={{ '--tw-ring-color': agent.color, animationDelay: `${idx * 0.3}s` }}
                            />
                            <div 
                              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: agent.color }}
                            >
                              <div className="w-2 h-2 bg-white rounded-full animate-online-pulse" />
                            </div>
                          </div>
                        ) : (
                          <div
                            className={`w-14 h-14 rounded-xl ${agent.bgColor} flex items-center justify-center`}
                          >
                            <Icon className="w-7 h-7" style={{ color: agent.color }} strokeWidth={1.5} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground text-sm">{agent.name}</h3>
                        <p className="text-muted-foreground text-xs mt-1 leading-relaxed">{agent.desc}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-1 shrink-0 mt-1 transition-all duration-300" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}

          </div>
        </section>

        {/* Locked Agents - only show if any */}
        {LOCKED_AGENTS.length > 0 && (
        <section>
          <h2 className="text-xl font-bold text-[#1F2937] font-heading mb-4">Bientot disponible</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LOCKED_AGENTS.map((agent) => {
              const Icon = agent.icon;
              return (
                <Card
                  key={agent.key}
                  className="border-slate-200/60 shadow-sm rounded-xl opacity-60 cursor-pointer hover:opacity-80"
                  onClick={() => setNotifyDialog(agent.key)}
                  data-testid={`locked-agent-${agent.key}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                        <Icon className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[#1F2937] text-sm">{agent.name}</h3>
                          <Lock className="w-3 h-3 text-slate-400" />
                        </div>
                        <p className="text-[#6B7280] text-xs mt-1">{agent.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
        )}

        {/* Resources - show for all */}
        <section>
          <h2 className="text-xl font-bold text-[#1F2937] dark:text-white font-heading mb-4">Ressources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {RESOURCES.map((res, i) => {
              const Icon = res.icon;
              return (
                <a
                  key={i}
                  href={res.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`resource-link-${i}`}
                  className="flex items-center gap-3 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-[#16233B]/20 hover:shadow-sm group"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#16233B]/5 dark:bg-[#16233B]/20 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#16233B] dark:text-[#FF8C42]" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-medium text-[#1F2937] dark:text-white">{res.label}</span>
                    {res.sublabel && <span className="text-xs text-[#6B7280] dark:text-slate-400 ml-1">{res.sublabel}</span>}
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-300 dark:text-slate-500 group-hover:text-[#16233B] dark:group-hover:text-[#FF8C42] transition-colors" />
                </a>
              );
            })}
            {(d.program === "odyss_mentoring" || isAdmin) && (
              <a
                href="https://odys-mentoring.odysinvest.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mentoring-link"
                className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[#FF8C42]/30 hover:shadow-sm group"
              >
                <div className="w-9 h-9 rounded-lg bg-[#FF8C42]/10 flex items-center justify-center shrink-0">
                  <GraduationCap className="w-4 h-4 text-[#FF8C42]" strokeWidth={1.5} />
                </div>
                <span className="text-sm font-medium text-[#1F2937] flex-1">Coaching avec Ismael (Mentoring)</span>
                <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#FF8C42] transition-colors" />
              </a>
            )}
          </div>
        </section>
        
        {/* Footer with CGU */}
        <footer className="mt-12 pt-6 border-t border-slate-100">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
            <p>Odys'<span className="text-[#F5B731]">IA</span> - Outil d'aide a la decision a caractere informatif</p>
            <div className="flex items-center gap-4">
              <a href="/cgu" className="hover:text-[#16233B] underline transition-colors">
                Conditions d'utilisation
              </a>
              <span>•</span>
              <span>L'IA peut se tromper. Verifiez toujours.</span>
            </div>
          </div>
        </footer>
      </main>

      {/* Notify Dialog */}
      <Dialog open={!!notifyDialog} onOpenChange={() => setNotifyDialog(null)}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Agent bientot disponible</DialogTitle>
            <DialogDescription>
              Cet expert sera disponible prochainement. Laisse ton email pour etre notifie du lancement.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="notify-email-input"
            type="email"
            placeholder="ton@email.com"
            value={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.value)}
            className="h-11 rounded-lg"
          />
          <DialogFooter>
            <Button
              onClick={handleNotify}
              data-testid="notify-submit-btn"
              className="bg-[#16233B] hover:bg-[#162B47] text-white rounded-lg"
            >
              Me notifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* PWA Install Prompt - shown after onboarding on dashboard */}
      <InstallPWA />
    </div>
  );
}
